/**
 * Command schemas.
 *
 * A command is the only thing in this system that is written to disk, sent over a socket, and
 * replayed months later. That makes it the value most likely to arrive from a build that is not
 * this one — an older journal, a newer client, a record damaged halfway through a write — and the
 * only place where "it was valid when we wrote it" is not an argument.
 *
 * Same conventions as `entities.ts`, for the same reasons: `.strict()` throughout, no annotation
 * of the schemas with their TypeScript counterparts (`exactOptionalPropertyTypes` makes that
 * impossible without a cast that would silence the check it claims to make), and drift caught by
 * `__tests__/drift.test-d.ts` instead — including the assertion that every command *kind* has a
 * schema, which is the one that matters when a twenty-first command is added.
 */
import { CLEARABLE_EDGE_FIELDS } from '@orbit-mind/editor-commands';
import * as z from 'zod';
import { edgeSchema, edgeShapeSchema, layoutIdSchema, mapNodeSchema, nodeShapeSchema, nodeSideSchema, nodeStyleSchema, nodeTypeSchema, orderKeySchema, richTextSchema, } from './entities';
import { attachmentIdSchema, edgeIdSchema, nodeIdSchema, operationIdSchema, themeIdSchema, userIdSchema, } from './ids';
import { finiteNumber, pointSchema, schemaVersionSchema, timestampSchema } from './primitives';
/**
 * The newest command format this build understands.
 *
 * Kept here rather than imported from `@orbit-mind/editor-commands`, because this package must
 * not depend on it at runtime — but the two numbers have to agree, and
 * `__tests__/commands.test.ts` asserts that they do.
 */
export const SUPPORTED_COMMAND_VERSION = 1;
/**
 * Provenance, on every command.
 *
 * The version bound is deliberate. A journal entry from a future build is not a malformed value
 * to be repaired, it is a record this code cannot faithfully interpret, and applying it anyway
 * would corrupt the document with something that looked like an ordinary edit.
 */
export const commandMetaSchema = z
    .object({
    operationId: operationIdSchema,
    timestamp: timestampSchema,
    authorId: userIdSchema,
    schemaVersion: schemaVersionSchema.refine((value) => value <= SUPPORTED_COMMAND_VERSION, {
        error: `command schemaVersion is newer than this build supports (${String(SUPPORTED_COMMAND_VERSION)})`,
    }),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// payloads
// ---------------------------------------------------------------------------------------------
export const createNodePayloadSchema = z
    .object({
    nodeId: nodeIdSchema,
    parentId: nodeIdSchema,
    order: orderKeySchema,
    text: z.string().optional(),
    richText: richTextSchema.optional(),
    side: nodeSideSchema.optional(),
    type: nodeTypeSchema.optional(),
    position: pointSchema.optional(),
})
    .strict();
export const deleteNodePayloadSchema = z.object({ nodeId: nodeIdSchema }).strict();
/**
 * The inverse of a deletion, carrying the whole subtree.
 *
 * By the time this replays, nothing else knows what those nodes were — which is why the payload
 * is fat, and why a damaged one is unrecoverable rather than merely inconvenient.
 */
export const restoreNodesPayloadSchema = z
    .object({
    nodes: z.array(mapNodeSchema),
    edges: z.array(edgeSchema),
})
    .strict();
export const moveNodePayloadSchema = z
    .object({
    nodeId: nodeIdSchema,
    position: pointSchema,
    pinned: z.boolean().optional(),
})
    .strict();
export const reparentNodePayloadSchema = z
    .object({
    nodeId: nodeIdSchema,
    newParentId: nodeIdSchema,
    order: orderKeySchema,
})
    .strict();
export const reorderNodePayloadSchema = z
    .object({ nodeId: nodeIdSchema, order: orderKeySchema })
    .strict();
export const changeNodeSidePayloadSchema = z
    .object({ nodeId: nodeIdSchema, side: nodeSideSchema })
    .strict();
export const updateNodeTextPayloadSchema = z
    .object({ nodeId: nodeIdSchema, richText: richTextSchema })
    .strict();
export const updateNodeStylePayloadSchema = z
    .object({
    nodeId: nodeIdSchema,
    style: nodeStyleSchema,
    shape: nodeShapeSchema.optional(),
    replace: z.boolean().optional(),
})
    .strict();
export const setCollapsedPayloadSchema = z
    .object({ nodeId: nodeIdSchema, collapsed: z.boolean() })
    .strict();
export const createEdgePayloadSchema = z
    .object({
    edgeId: edgeIdSchema,
    sourceNodeId: nodeIdSchema,
    targetNodeId: nodeIdSchema,
    label: z.string().optional(),
})
    .strict();
export const deleteEdgePayloadSchema = z.object({ edgeId: edgeIdSchema }).strict();
export const restoreEdgesPayloadSchema = z.object({ edges: z.array(edgeSchema) }).strict();
/**
 * A partial edge update.
 *
 * Derived from `edgeSchema` rather than restated, so a field added to an edge is covered here the
 * moment it exists. `id`, `mapId` and `createdAt` are omitted because changing any of them would
 * make the command a different edge rather than an edit to this one.
 */
export const updateEdgePayloadSchema = z
    .object({
    edgeId: edgeIdSchema,
    changes: edgeShapeSchema
        .omit({ id: true, mapId: true, createdAt: true })
        .partial()
        // The whole-edge schema's self-link rule cannot apply to a patch that carries only one
        // endpoint. When it carries both, it can, and a rewiring that would join a node to itself
        // is the same broken edge whether it arrives whole or as a change.
        .refine((changes) => changes.sourceNodeId === undefined ||
        changes.targetNodeId === undefined ||
        changes.sourceNodeId !== changes.targetNodeId, { error: 'an edge must not connect a node to itself' }),
    /**
     * Fields to remove rather than set.
     *
     * A merge cannot express "this key was absent", which is why a connection's colour could be
     * changed and never reset to the theme's accent. Enumerated rather than a free list of
     * strings: the only fields worth clearing are the ones an edge can be without.
     */
    clear: z.array(z.enum(CLEARABLE_EDGE_FIELDS)).optional(),
})
    .strict();
export const attachmentPayloadSchema = z
    .object({ nodeId: nodeIdSchema, attachmentId: attachmentIdSchema })
    .strict();
/**
 * One layout pass, not one command per node.
 *
 * The record is keyed by node id, so it is validated as a string map: a branded key type would
 * describe a constraint the runtime cannot check anyway, and the ids are checked where they are
 * used against the document.
 */
/** Shared by the two commands that move a set of nodes and say why. */
const layoutPositionsSchema = z.record(z.string(), z
    .object({
    x: finiteNumber,
    y: finiteNumber,
    width: finiteNumber.nonnegative().optional(),
    height: finiteNumber.nonnegative().optional(),
})
    .strict());
export const applyLayoutPayloadSchema = z
    .object({
    layoutId: layoutIdSchema,
    positions: layoutPositionsSchema,
    scopeNodeId: nodeIdSchema.optional(),
})
    .strict();
/**
 * The plain fields of a node.
 *
 * `clear` is a list of keys rather than `undefined` values in `changes`, for the reason the
 * payload states: a merge cannot express absence, and undo needs to.
 */
export const updateNodePayloadSchema = z
    .object({
    nodeId: nodeIdSchema,
    changes: z
        .object({
        emoji: z.string().max(16).optional(),
        icon: z.string().max(16).optional(),
        notes: richTextSchema.optional(),
        width: z.number().nonnegative().optional(),
        height: z.number().nonnegative().optional(),
        pinned: z.boolean().optional(),
        status: mapNodeSchema.shape.status,
        priority: mapNodeSchema.shape.priority,
        progress: mapNodeSchema.shape.progress,
        dueDate: mapNodeSchema.shape.dueDate,
        metadata: mapNodeSchema.shape.metadata,
    })
        .strict(),
    clear: z
        .array(z.enum([
        'emoji',
        'icon',
        'notes',
        'pinned',
        'status',
        'priority',
        'progress',
        'dueDate',
        'metadata',
    ]))
        .optional(),
})
    .strict();
export const clearBranchLayoutPayloadSchema = z
    .object({ nodeId: nodeIdSchema, positions: layoutPositionsSchema })
    .strict();
export const applyThemePayloadSchema = z.object({ themeId: themeIdSchema }).strict();
// The same bound as `mindMapSchema.title`, because a command that produced a document the entity
// schema rejects would write a map that cannot be read back.
export const renameMapPayloadSchema = z.object({ title: z.string().max(500) }).strict();
export const groupNodesPayloadSchema = z
    .object({
    groupId: nodeIdSchema,
    nodeIds: z.array(nodeIdSchema),
    order: orderKeySchema,
    title: z.string().optional(),
})
    .strict();
export const ungroupNodesPayloadSchema = z
    .object({
    groupId: nodeIdSchema,
    orders: z.record(z.string(), orderKeySchema),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// the command union
// ---------------------------------------------------------------------------------------------
function command(kind, payload) {
    return z.object({ kind: z.literal(kind), meta: commandMetaSchema, payload }).strict();
}
/**
 * A discriminated union, matching the `switch` in `editor-commands`.
 *
 * Discriminated rather than a plain union so a failure reports "unknown kind" or the one
 * offending field, instead of twenty parallel failures the reader has to diff by eye.
 */
export const commandSchema = z.discriminatedUnion('kind', [
    command('create-node', createNodePayloadSchema),
    command('delete-node', deleteNodePayloadSchema),
    command('restore-nodes', restoreNodesPayloadSchema),
    command('move-node', moveNodePayloadSchema),
    command('reparent-node', reparentNodePayloadSchema),
    command('reorder-node', reorderNodePayloadSchema),
    command('change-node-side', changeNodeSidePayloadSchema),
    command('update-node-text', updateNodeTextPayloadSchema),
    command('update-node-style', updateNodeStylePayloadSchema),
    command('set-collapsed', setCollapsedPayloadSchema),
    command('create-edge', createEdgePayloadSchema),
    command('delete-edge', deleteEdgePayloadSchema),
    command('restore-edges', restoreEdgesPayloadSchema),
    command('update-edge', updateEdgePayloadSchema),
    command('add-attachment', attachmentPayloadSchema),
    command('remove-attachment', attachmentPayloadSchema),
    command('apply-layout', applyLayoutPayloadSchema),
    command('apply-theme', applyThemePayloadSchema),
    command('clear-branch-layout', clearBranchLayoutPayloadSchema),
    command('update-node', updateNodePayloadSchema),
    command('rename-map', renameMapPayloadSchema),
    command('group-nodes', groupNodesPayloadSchema),
    command('ungroup-nodes', ungroupNodesPayloadSchema),
]);
/** The kinds this build can read, for a caller that needs to report what it rejected. */
export const COMMAND_KINDS = commandSchema.options.map((option) => option.shape.kind.value);
//# sourceMappingURL=commands.js.map